import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from "react";
import { useAccounts } from "../hooks/useApi";
import type { AccountListItem } from "../types";

interface AccountContextValue {
  accounts: AccountListItem[];
  loading: boolean;
  selectedAccountUuid: string | null;
  selectedAccount: AccountListItem | null;
  selectAccount: (uuid: string | null) => void;
  /** Re-fetch accounts from the API (call after creating a new account). */
  refetchAccounts: () => void;
}

const AccountContext = createContext<AccountContextValue | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
  const { accounts, loading, refetch } = useAccounts();
  const [selectedAccountUuid, setSelectedAccountUuid] = useState<string | null>(
    null
  );

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.uuid === selectedAccountUuid) ?? null,
    [accounts, selectedAccountUuid]
  );

  const refetchAccounts = useCallback(() => {
    refetch();
  }, [refetch]);

  const value = useMemo<AccountContextValue>(
    () => ({
      accounts,
      loading,
      selectedAccountUuid,
      selectedAccount,
      selectAccount: setSelectedAccountUuid,
      refetchAccounts,
    }),
    [accounts, loading, selectedAccountUuid, selectedAccount, refetchAccounts]
  );

  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
}

export function useAccountContext(): AccountContextValue {
  const ctx = useContext(AccountContext);
  if (!ctx) {
    throw new Error("useAccountContext must be used within <AccountProvider>");
  }
  return ctx;
}
